import React from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

/**
 * AgreementCheckbox — gold/black themed checkbox used at the bottom of every
 * NoFee registration form (Restaurant Step 1, Partner signup, Customer
 * signup, Rewards opt-in). The label always carries a link to the canonical
 * legal page, so the user can read the document before ticking.
 *
 * Props:
 *   • checked          — controlled boolean state
 *   • onChange         — (next: boolean) => void
 *   • agreementLabel   — short title of the document, e.g. "Restaurant Agreement"
 *   • agreementHref    — internal route, e.g. "/restaurant-agreement"
 *   • testid           — required, e.g. "restaurant-agreement-checkbox"
 *   • errorText        — optional inline validation message
 */
export default function AgreementCheckbox({
  checked,
  onChange,
  agreementLabel,
  agreementHref,
  testid,
  errorText = '',
}) {
  return (
    <div className="space-y-2">
      <label
        className="flex items-start gap-3 group cursor-pointer select-none"
        data-testid={`${testid}-label`}
      >
        <span
          className={[
            'mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all shrink-0',
            checked
              ? 'bg-gold-500 border-gold-500'
              : 'bg-transparent border-white/30 group-hover:border-white/60',
            errorText ? 'border-rose-500' : '',
          ].join(' ')}
          aria-hidden="true"
        >
          {checked && <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />}
        </span>
        <input
          type="checkbox"
          checked={!!checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only"
          data-testid={testid}
        />
        <span className="text-[13px] leading-relaxed text-white/75">
          I have read and agree to the{' '}
          <Link
            to={agreementHref}
            target="_blank"
            rel="noopener"
            className="text-gold-300 hover:text-gold-200 underline underline-offset-2 font-semibold"
            data-testid={`${testid}-link`}
            onClick={(e) => e.stopPropagation()}
          >
            {agreementLabel}
          </Link>
          .
        </span>
      </label>
      {errorText && (
        <p
          className="text-[12px] text-rose-300 font-semibold pl-8"
          data-testid={`${testid}-error`}
        >
          {errorText}
        </p>
      )}
    </div>
  );
}
